import { Family, User, Task, Reward, Room, Tool, FamilyDeletion } from 'shared/types';
import { FamilyDto, RewardDto, EventDto } from 'shared/types/dto';
import type { StatsPeriod, StatsResponse, StatsVisibility } from 'shared/types/stats';
import { normalizeFamily } from './normalizers';
import { familyDeletionFrom } from '../utils/accountDeletion';
import { prepareImageForUpload } from '../utils/prepareImage';
import { withTimeout } from '../utils/withTimeout';
import { storageService } from './storageService';
import {
  supabase,
  is_supabase_configured,
  getSupabaseForApi,
  isMemberMode,
  setPinMemberSession,
  clearPinMemberSession,
  getMemberSupabase,
} from './supabaseClient';

// Store the JWT token (kept in sync with Supabase session or member JWT)
let authToken: string | null = null;
// Store the device token (for non-cookie environments)
let deviceToken: string | null = null;
// Cached family_id — set during login/init so PostgREST inserts can include it
let _cachedFamilyId: string | null = null;
export const setCachedFamilyId = (id: string | null): void => { _cachedFamilyId = id; };
export const getCachedFamilyId = (): string | null => _cachedFamilyId;

// Keep authToken in sync with Supabase session changes (token refresh, login, logout)
// When in member mode, don't overwrite — we use our stored member token
if (is_supabase_configured) {
  supabase.auth.onAuthStateChange((_event, session) => {
    if (session?.access_token && !isMemberMode()) {
      authToken = session.access_token;
    }
  });
}

// Fetch checklist images by checklist_item_id and return a map ci_id -> [url|image_data|id]
async function fetchChecklistImagesMap(ciIds: string[]): Promise<Record<string, string[]>> {
  const out: Record<string, string[]> = {};
  if (!is_supabase_configured || ciIds.length === 0) return out;
  const { data } = await getSupabaseForApi().from('images')
    .select('checklist_item_id, url, image_data, id')
    .in('checklist_item_id', ciIds);
  for (const img of data || []) {
    const key = img.checklist_item_id;
    if (!out[key]) out[key] = [];
    out[key].push(img.url || img.image_data || img.id);
  }
  return out;
}

// Authentication methods
export const login = async (email: string, password: string): Promise<{ token: string; user: any }> => {
  const { data: authData, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error || !authData.session) {
    throw new Error(error?.message || 'Login failed');
  }
  authToken = authData.session.access_token;
  storageService.setAuthToken(authToken);
  clearPinMemberSession();

  const user = await getCurrentUser();
  return { token: authToken, user };
};

// Device binding (guardian) - sets httpOnly cookie and also returns token for native apps
export const deviceBind = async (device_name?: string): Promise<{ device: any; device_token?: string }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('device-auth', {
    body: { action: 'bind', device_name: device_name || 'Web Browser' },
  });
  if (error) throw new Error(error.message);
  if (data.device_token) {
    deviceToken = data.device_token;
    storageService.setDeviceToken(data.device_token);
  }
  return data;
};

export const getDeviceSession = async (): Promise<{ device: any; members: Array<{ id: string; name: string; username: string | null; is_admin: boolean; member_type: 'adult' | 'child' }>; username_locks?: Array<{ username: string; failed_attempts: number; lock_until: string | null }> }> => {
  const stored = deviceToken || storageService.getDeviceToken();
  const { data, error } = await getSupabaseForApi().functions.invoke('device-auth', {
    body: { action: 'session' },
    headers: stored ? { 'x-device-token': stored } : undefined,
  });
  if (error) throw new Error(error.message);
  return data;
};

export const deviceLogout = async (): Promise<void> => {
  const stored = deviceToken || storageService.getDeviceToken();
  await getSupabaseForApi().functions.invoke('device-auth', {
    body: { action: 'logout' },
    headers: stored ? { 'x-device-token': stored } : undefined,
  });
  deviceToken = null;
  storageService.removeDeviceToken();
  return;
};

export const deviceUnlock = async (): Promise<void> => {
  const stored = deviceToken || storageService.getDeviceToken();
  const { error } = await getSupabaseForApi().functions.invoke('device-auth', {
    body: { action: 'unlock' },
    headers: stored ? { 'x-device-token': stored } : undefined,
  });
  if (error) throw new Error(error.message);
  return;
};

export const deviceUnlockUsername = async (username: string): Promise<void> => {
  const stored = deviceToken || storageService.getDeviceToken();
  const { error } = await getSupabaseForApi().functions.invoke('device-auth', {
    body: { action: 'unlock', username },
    headers: stored ? { 'x-device-token': stored } : undefined,
  });
  if (error) throw new Error(error.message);
  return;
};

async function getFunctionsErrorDetails(error: unknown): Promise<{ message: string; code?: string }> {
  if (error && typeof error === 'object' && 'context' in error) {
    try {
      const ctx = (error as { context?: unknown }).context;
      if (ctx && typeof (ctx as { json?: () => Promise<unknown> }).json === 'function') {
        const body = (await (ctx as { json: () => Promise<{ error?: string; code?: string }> }).json()) as { error?: string; code?: string };
        if (body?.error) return { message: String(body.error), code: body.code ? String(body.code) : undefined };
      }
    } catch {}
  }
  return { message: (error as Error)?.message ?? 'Unknown error' };
}

async function getFunctionsErrorMessage(error: unknown): Promise<string> {
  return (await getFunctionsErrorDetails(error)).message;
}

/** Turn a functions.invoke error into an ApiError carrying the server's message and code. */
async function toApiError(error: unknown): Promise<ApiError> {
  const { message, code } = await getFunctionsErrorDetails(error);
  return new ApiError(message, code);
}

/** Error thrown by Edge Function calls that return a machine-readable `code` (e.g. user-profile). */
export class ApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

export const memberLoginApi = async (username: string, pin_or_password: string): Promise<{ token: string; user: any }> => {
  const stored = deviceToken || storageService.getDeviceToken();
  const { data, error } = await getSupabaseForApi().functions.invoke('member-auth', {
    body: { action: 'login', username, pin_or_password },
    headers: stored ? { 'x-device-token': stored } : undefined,
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  const token = data.token;
  // member-auth returns a real Supabase session (docs/design/pin-member-auth.md); keep it in the
  // member's own client so it renews itself.
  if (!data.session?.access_token || !data.session?.refresh_token) {
    throw new Error('Login failed: no session returned');
  }
  const { error: sessErr } = await getMemberSupabase().auth.setSession({
    access_token: data.session.access_token,
    refresh_token: data.session.refresh_token,
  });
  if (sessErr) throw new Error(sessErr.message);
  authToken = token;
  storageService.setAuthToken(token);
  setPinMemberSession();
  return data;
};

export const memberLogoutApi = async (): Promise<void> => {
  const stored = deviceToken || storageService.getDeviceToken();
  await getSupabaseForApi().functions.invoke('member-auth', {
    body: { action: 'logout' },
    headers: stored ? { 'x-device-token': stored } : undefined,
  });
  return;
};

export const register = async (name: string, email: string, password: string, familyName: string): Promise<{ token: string; user: any }> => {
  return registerWithFamily(name, email, password, familyName);
};

export const registerWithFamily = async (
  name: string,
  email: string,
  password: string,
  family_name: string,
  username?: string,
  beta_code?: string
): Promise<{ token: string; user: any }> => {
  const { data: { session: existingSession } } = await supabase.auth.getSession();
  if (existingSession?.access_token && password === '') {
    authToken = existingSession.access_token;
    storageService.setAuthToken(authToken);
    clearPinMemberSession();
    const { data, error: regErr } = await getSupabaseForApi().functions.invoke('complete-registration', {
      body: { name, family_name, username, beta_code },
    });
    if (regErr) throw new Error(await getFunctionsErrorMessage(regErr));
    return { token: authToken, user: data.user };
  }

  const { data: authData, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { name } },
  });
  if (error) throw new Error(error.message);
  if (!authData.session) {
    throw new Error('Registration succeeded but no session returned. Check email confirmation settings.');
  }

  authToken = authData.session.access_token;
  storageService.setAuthToken(authToken);
  clearPinMemberSession();

  const { data, error: regErr } = await getSupabaseForApi().functions.invoke('complete-registration', {
    body: { name, family_name, username, beta_code },
  });
  if (regErr) throw new Error(await getFunctionsErrorMessage(regErr));
  return { token: authToken, user: data.user };
};

/** Pre-signup check (public, no auth) — lets Signup.tsx fail fast before creating an auth account. */
export const checkBetaCode = async (code: string): Promise<boolean> => {
  if (!is_supabase_configured) throw new Error('Beta access check requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('beta-access', {
    body: { action: 'check', code },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return !!(data as { valid?: boolean })?.valid;
};

export const logout = async (): Promise<void> => {
  authToken = null;
  localStorage.removeItem('family_task_manager_token');
  clearPinMemberSession();
  await supabase.auth.signOut();
};

export const getCurrentUser = async (): Promise<any> => {
  if (isMemberMode()) {
    const token = authToken || storageService.getAuthToken();
    if (!token) throw new Error('Not authenticated');
    const { data, error } = await getSupabaseForApi().functions.invoke('get-current-user');
    if (error) throw new Error(error.message);
    if (!data?.user) throw new Error('Invalid response from get-current-user');
    return data.user;
  }
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { data, error } = await supabase.from('users')
    .select(`id, family_id, name, email, username, is_admin, member_type, points, coins, coins, avatar_url`)
    .eq('id', user.id)
    .single();
  if (error) throw new Error(error.message);
  // Real Supabase Auth identities (nothing ever wrote to the old custom auth_identities table,
  // so has_*_identity was always false regardless of how the user actually signed up/linked).
  const providers = new Set((user.identities || []).map((i) => i.provider));
  const email = data?.email ?? user.email ?? undefined;
  // A family waiting to be deleted: the app shows only the "scheduled for deletion" screen.
  let family_deletion: FamilyDeletion | null = null;
  if (data?.family_id) {
    const { data: fam } = await supabase.from('families')
      .select('name, deletion_requested_at').eq('id', data.family_id).maybeSingle();
    if (fam?.deletion_requested_at) family_deletion = familyDeletionFrom(fam.deletion_requested_at, fam.name || '');
  }
  return {
    ...data,
    email,
    family_deletion,
    stars: data.coins,
    has_google_identity: providers.has('google'),
    has_facebook_identity: providers.has('facebook'),
    has_apple_identity: providers.has('apple'),
  };
};

export const initializeAuth = (token: string): void => {
  authToken = token;
};

export const clearAuth = async (): Promise<void> => {
  authToken = null;
  clearPinMemberSession();
  await supabase.auth.signOut();
};

export const restoreSupabaseSession = async (): Promise<string | null> => {
  if (!is_supabase_configured) return null;
  const { data } = await supabase.auth.getSession();
  if (data.session?.access_token) {
    authToken = data.session.access_token;
    return authToken;
  }
  return null;
};

// Password reset
export const requestPasswordReset = async (email: string, _locale?: string): Promise<void> => {
  const redirectTo = `${window.location.origin}/reset-password`;
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
  if (error) throw new Error(error.message);
  return;
};

export const resetPassword = async (token_or_unused: string, new_password: string): Promise<void> => {
  const { error } = await supabase.auth.updateUser({ password: new_password });
  if (error) throw new Error(error.message);
  return;
};

// Reward methods
export const claimReward = async (rewardId: string): Promise<{ message: string; reward: RewardDto }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('reward-claim', {
    body: { reward_id: rewardId },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data;
};

/** Grown-ups: a claimed reward was handed over (done=false puts it back on the list). */
export const setRewardHandedOver = async (rewardId: string, done: boolean): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('reward-claim', {
    body: { reward_id: rewardId, action: done ? 'hand-over' : 'undo-hand-over' },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
};

export const createReward = async (rewardData: any): Promise<Reward> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('family-mutate', {
    body: { entity: 'reward', action: 'create', data: rewardData },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data.reward as Reward;
};

export const updateReward = async (rewardId: string, rewardData: any): Promise<Reward> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('family-mutate', {
    body: { entity: 'reward', action: 'update', id: rewardId, data: rewardData },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data.reward as Reward;
};

export const deleteReward = async (id: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('family-mutate', {
    body: { entity: 'reward', action: 'delete', id },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

// Family methods
export const getFamily = async (completedLimit?: number): Promise<Family> => {
  // Single source of truth: always load the full family payload via the get-family Edge
  // Function (service role). Works in both guardian and PIN-member mode, and keeps one
  // implementation of the family assembly (incl. eligible_points and checklist state).
  // completedLimit: how many finished (verified) tasks to include — server default 20.
  const { data, error } = await getSupabaseForApi().functions.invoke('get-family', {
    body: completedLimit ? { completed_limit: completedLimit } : {},
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  if (!data?.family) throw new Error('Invalid response from get-family');
  if (data.family.id) setCachedFamilyId(data.family.id);
  return normalizeFamily(data.family as FamilyDto);
};

export const updateFamily = async (name: string): Promise<Family> => {
  if (!_cachedFamilyId) throw new Error('No family associated with this user');
  const { data, error } = await getSupabaseForApi().from('families')
    .update({ name, updated_at: new Date().toISOString() })
    .eq('id', _cachedFamilyId)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as Family;
};

/** Called once by OnboardingWizard on finish — the explicit signal App.tsx uses to leave onboarding. */
export const markOnboardingComplete = async (): Promise<Family> => {
  if (!_cachedFamilyId) throw new Error('No family associated with this user');
  const { data, error } = await getSupabaseForApi().from('families')
    .update({ onboarding_completed_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', _cachedFamilyId)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as Family;
};

export const addFamilyMember = async (
  name: string,
  username: string,
  pin: string,
  email?: string,
  isAdmin: boolean = false,
  memberType?: 'adult' | 'child',
  /** A child's age group (2026-10-15), e.g. 'age_6_7'. */
  ageGroup?: string | null,
): Promise<User> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('member-manage', {
    body: { action: 'add', name, username, pin, email, is_admin: isAdmin, member_type: memberType, age_group: ageGroup ?? undefined },
  });
  if (error) throw new Error(error.message);
  return data.user;
};

export const updateFamilyMember = async (
  id: string,
  updates: { member_type?: 'adult' | 'child'; name?: string; pin?: string; is_admin?: boolean; age_group?: string | null }
): Promise<User> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('member-manage', {
    body: { action: 'update', member_id: id, ...updates },
  });
  if (error) throw new Error(error.message);
  return data.user;
};

// Deleting a member / the family (account-maintenance). Undo is possible for 30 days.
const accountAction = async (body: Record<string, unknown>): Promise<{ permanent_at?: string }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('account-maintenance', { body });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data || {};
};
export const deleteFamilyMember = (memberId: string) => accountAction({ action: 'delete-member', member_id: memberId });
export const restoreFamilyMember = (memberId: string) => accountAction({ action: 'restore-member', member_id: memberId });
export const deleteFamily = (confirmName: string) => accountAction({ action: 'delete-family', confirm_name: confirmName });
export const restoreFamily = () => accountAction({ action: 'restore-family' });

// Task methods
export const getTasks = async (filters?: { available?: boolean; completed?: boolean }): Promise<any[]> => {
  let q = getSupabaseForApi().from('tasks')
    .select('*, checklist_items(*), task_tools(tool_id)')
    .order('created_at', { ascending: false });

  if (filters?.available === true) {
    q = q.eq('is_available', true).eq('is_completed', false);
  }
  if (filters?.completed === true) {
    q = q.eq('is_completed', true);
  }

  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const allCiIds = (data || []).flatMap((t: any) => (t.checklist_items || []).map((ci: any) => ci.id));
  const imagesByCi = await fetchChecklistImagesMap(allCiIds);
  return (data || []).map((t: any) => ({
    ...t,
    checklist: (t.checklist_items || []).sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0)).map((ci: any) => ({
      ...ci,
      images: imagesByCi[ci.id] || (ci.images || []).map((img: any) => img?.url || img?.image_data || img?.id).filter(Boolean),
    })),
    tools: (t.task_tools || []).map((tt: any) => ({ id: tt.tool_id })),
  }));
};

export const getTask = async (id: string): Promise<Task> => {
  const { data, error } = await getSupabaseForApi().from('tasks')
    .select('*, checklist_items(*), task_tools(tool_id)')
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  const ciIds = (data.checklist_items || []).map((ci: any) => ci.id);
  const imagesByCi = await fetchChecklistImagesMap(ciIds);
  return {
    ...data,
    checklist: (data.checklist_items || []).sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0)).map((ci: any) => ({
      ...ci,
      images: imagesByCi[ci.id] || (ci.images || []).map((img: any) => img?.url || img?.image_data || img?.id).filter(Boolean),
    })),
    tools: (data.task_tools || []).map((tt: any) => ({ id: tt.tool_id })),
  } as Task;
};

export const createTask = async (taskData: any): Promise<Task> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('task-mutate', {
    body: { action: 'create', ...taskData },
  });
  if (error) throw new Error(error.message);
  return data.task;
};

export const updateTask = async (id: string, taskData: any): Promise<Task> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('task-mutate', {
    body: { action: 'update', task_id: id, ...taskData },
  });
  if (error) throw new Error(error.message);
  return data.task;
};

// Task templates API
export type AgeGroupCode = 'all_ages' | 'age_2_3' | 'age_4_5' | 'age_6_7' | 'age_8_9' | 'age_10_12' | 'age_13_plus' | 'adults';

export const listTaskTemplatesApi = async (
  language: string,
  opts: {
    age_group?: AgeGroupCode | AgeGroupCode[];
    limit?: number;
    offset?: number;
    /** Platform standard tasks only (master admin catalog). */
    system_only?: boolean;
    /** This family’s saved templates only; not visible to other families (RLS). */
    family_only?: boolean;
  } = {},
): Promise<any[]> => {
  // Always via the Edge Function (guardians and PIN members alike): it filters per family,
  // skips switched-off tasks and falls back to English for untranslated standard tasks.
  const { data, error } = await getSupabaseForApi().functions.invoke('task-template', {
    body: {
      action: 'list',
      language_code: language,
      age_group: opts.age_group,
      limit: opts.limit,
      offset: opts.offset,
      system_only: opts.system_only,
      family_only: opts.family_only,
    },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return (data as { templates?: any[] })?.templates ?? [];
};

export const createFamilyTaskTemplateApi = async (payload: {
  language_code: string;
  title: string;
  description?: string;
  room_id?: string;
  points: number;
  recurrence_days?: number;
  header_image_url?: string;
  checklist_items?: Array<{ text: string; order_index?: number; images?: string[]; timer_seconds?: number }>;
  age_group?: AgeGroupCode;
}): Promise<any> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('task-template', {
    body: { action: 'create', ...payload },
  });
  if (error) throw new Error(error.message);
  return data.template;
};

export const updateFamilyTaskTemplateApi = async (id: string, payload: Partial<{ title: string; description: string; room_id: string; points: number; recurrence_days: number; header_image_url: string; checklist_items: any[]; language_code: string; age_group: AgeGroupCode }>): Promise<any> => {
  const updateData: Record<string, any> = { ...payload };
  if (payload.checklist_items !== undefined) {
    updateData.checklist_items = JSON.stringify(payload.checklist_items);
  }
  const { data, error } = await getSupabaseForApi().from('task_templates')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const deleteFamilyTaskTemplateApi = async (id: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('task-template', {
    body: { action: 'delete', template_id: id },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

export const instantiateTaskFromTemplateApi = async (id: string): Promise<Task> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('task-template', {
    body: { action: 'use', template_id: id },
  });
  if (error) throw new Error(error.message);
  return data.task;
};

export const deleteTask = async (id: string, opts: { wholeGroup?: boolean } = {}): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('task-mutate', {
    // wholeGroup: a shared task goes for everyone (2026-10-14)
    body: { action: 'delete', task_id: id, ...(opts.wholeGroup ? { whole_group: true } : {}) },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

/** Prefs shape for Settings before any row has been saved. */
export const DEFAULT_NOTIFICATION_PREFS = {
  daily_digest_enabled: false,
  digest_hour_local: 7,
  event_alerts_enabled: false,
  quiet_hours_start: '',
  quiet_hours_end: '',
  task_review_ready_enabled: true,
  task_approved_enabled: true,
  reward_claimable_enabled: true,
  reward_claimed_enabled: true,
  task_due_soon_enabled: true,
  task_overdue_enabled: true,
  new_task_available_enabled: false,
} as const;

// Notifications — routed through notification-manage so both guardians and PIN members
// (custom JWT, no auth.uid()) can subscribe/manage prefs; see getAppUser in the Edge Function.
export const registerWebPushSubscription = async (subscription: PushSubscription, timezone?: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('notification-manage', {
    body: { action: 'subscribe', subscription, timezone },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

export const unregisterWebPushSubscription = async (): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('notification-manage', {
    body: { action: 'unsubscribe' },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

export const getNotificationPrefs = async (): Promise<any> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('notification-manage', {
    body: { action: 'get-prefs' },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data?.prefs ?? { ...DEFAULT_NOTIFICATION_PREFS };
};

export const updateNotificationPrefs = async (prefs: any): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('notification-manage', {
    body: { action: 'update-prefs', prefs },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

/** Who decides a child's notifications (step 6c): the child, or a grown-up (always on / off). */
export type ParentControl = 'child' | 'on' | 'off';

export const listNotificationControls = async (): Promise<Array<{ user_id: string; parent_control: ParentControl }>> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('notification-manage', {
    body: { action: 'list-controls' },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data?.controls ?? [];
};

export const setNotificationControl = async (memberId: string, parentControl: ParentControl): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('notification-manage', {
    body: { action: 'set-control', member_id: memberId, parent_control: parentControl },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
};

export const addEventReminder = async (eventId: string, offset_minutes: number, message_override?: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('event-mutate', {
    body: { action: 'add-reminder', event_id: eventId, offset_minutes, message_override },
  });
  if (error) throw await toApiError(error);
  return;
};

export const clearEventReminders = async (eventId: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('event-mutate', {
    body: { action: 'clear-reminders', event_id: eventId },
  });
  if (error) throw await toApiError(error);
  return;
};

export const claimTask = async (id: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('task-action', {
    body: { action: 'claim', task_id: id },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

// Calendar methods
export const listEvents = async (params: { from?: string; to?: string; member_id?: string } = {}): Promise<EventDto[]> => {
  let events: any[];
  if (isMemberMode()) {
    // PostgREST rejects member JWTs; read via the Edge Function (service role).
    const { data, error } = await getSupabaseForApi().functions.invoke('event-mutate', {
      body: { action: 'list', from: params.from, to: params.to },
    });
    if (error) throw await toApiError(error);
    events = data?.events || [];
  } else {
    let q = getSupabaseForApi().from('events')
      .select('*, event_attendees(user_id), event_reminders(*)')
      .order('start_at');

    if (params.from) q = q.gte('start_at', params.from);
    if (params.to) q = q.lte('start_at', params.to);

    const { data, error } = await q;
    if (error) throw new Error(error.message);

    events = (data || []).map((e: any) => ({
      ...e,
      attendees: (e.event_attendees || []).map((a: any) => a.user_id),
      reminders: e.event_reminders || [],
    }));
  }

  // Client-side filter for member_id (attendee)
  if (params.member_id) {
    events = events.filter((e: any) =>
      (e.attendees as string[]).includes(params.member_id!)
    );
  }

  return events as EventDto[];
};

export const createEventApi = async (payload: Partial<EventDto> & { title: string; start_at: string; end_at: string; all_day?: boolean; attendees?: string[] }): Promise<EventDto> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('event-mutate', {
    body: { action: 'create', ...payload },
  });
  if (error) throw await toApiError(error);
  return data.event;
};

export const updateEventApi = async (id: string, payload: Partial<EventDto>): Promise<EventDto> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('event-mutate', {
    body: { action: 'update', event_id: id, ...payload },
  });
  if (error) throw await toApiError(error);
  return data.event;
};

export const deleteEventApi = async (id: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('event-mutate', {
    body: { action: 'delete', event_id: id },
  });
  if (error) throw await toApiError(error);
  return;
};

export const uploadEventImageApi = async (eventId: string, file: File): Promise<EventDto> => {
  const jpeg = await prepareImageForUpload(file);
  const signed = await signUpload({ intent: 'event_image', content_type: 'image/jpeg', event_id: eventId });
  await putToSignedUrl(signed.upload_url, signed.headers, jpeg);
  const { data, error } = await getSupabaseForApi().functions.invoke('event-mutate', {
    body: { action: 'set-image', event_id: eventId, header_image: signed.public_url },
  });
  if (error) throw await toApiError(error);
  return data.event as EventDto;
};

export const removeEventImageApi = async (eventId: string): Promise<EventDto> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('event-mutate', {
    body: { action: 'set-image', event_id: eventId, header_image: null },
  });
  if (error) throw await toApiError(error);
  return data.event as EventDto;
};

export const unclaimTask = async (id: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('task-action', {
    body: { action: 'unclaim', task_id: id },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

export const fixTaskAvailability = async (): Promise<{ message: string; updatedCount: number }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('task-action', {
    body: { action: 'fix-availability', task_id: '_' },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data;
};

/** Finish a task. `auto_approved`: a grown-up's own task that was approved at once (2026-10-19). */
export const submitTaskForVerification = async (id: string, completion_message: string): Promise<{ auto_approved: boolean }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('task-action', {
    body: { action: 'submit', task_id: id, completion_message },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return { auto_approved: data?.auto_approved === true };
};

export type VerifyTaskResult = {
  message?: string;
  repaired?: boolean;
  already_verified?: boolean;
  misattributed_repair?: boolean;
  already_repaired_misattribution?: boolean;
};

/** approvalNote: a preset note the child sees on their approval screen (redesign D7). */
export type VerifyTaskOptions = { repairMisattributedPayout?: boolean; approvalNote?: 'so_neat' | 'well_done' | 'thank_you' | null };

export const verifyTask = async (
  id: string,
  opts?: VerifyTaskOptions,
): Promise<VerifyTaskResult | void> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('task-action', {
    body: {
      action: 'verify',
      task_id: id,
      ...(opts?.repairMisattributedPayout ? { repair_misattributed_payout: true } : {}),
      ...(opts?.approvalNote ? { approval_note: opts.approvalNote } : {}),
    },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return (data ?? {}) as VerifyTaskResult;
};

export const ackTaskVerification = async (id: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('task-action', {
    body: { action: 'ack-verification', task_id: id },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
};

export const updateChecklistState = async (taskId: string, itemId: string, isChecked: boolean): Promise<void> => {
  // Via task-action (service role, resolves the user from the token) so it works in
  // guardian AND PIN-member mode — a direct PostgREST upsert is rejected for member JWTs.
  const { error } = await getSupabaseForApi().functions.invoke('task-action', {
    body: { action: 'checklist', task_id: taskId, item_id: itemId, is_checked: isChecked },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

// Room methods
export const createRoom = async (roomData: any): Promise<Room> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('family-mutate', {
    body: { entity: 'room', action: 'create', data: roomData },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data.room as Room;
};

export const updateRoom = async (id: string, roomData: any): Promise<Room> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('family-mutate', {
    body: { entity: 'room', action: 'update', id, data: roomData },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data.room as Room;
};

/** A picture of the room (2026-10-13): shrunk to a JPEG, uploaded, then saved on the room. */
export const uploadRoomImageApi = async (roomId: string, file: File): Promise<Room> => {
  const jpeg = await prepareImageForUpload(file, 1200, 0.82);
  const signed = await signUpload({ intent: 'room_image', content_type: 'image/jpeg', room_id: roomId });
  await putToSignedUrl(signed.upload_url, signed.headers, jpeg);
  return updateRoom(roomId, { image_url: signed.public_url });
};

export const removeRoomImageApi = (roomId: string): Promise<Room> => updateRoom(roomId, { image_url: null });

export const deleteRoom = async (id: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('family-mutate', {
    body: { entity: 'room', action: 'delete', id },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

// Tool methods
export const createTool = async (toolData: any): Promise<Tool> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('family-mutate', {
    body: { entity: 'tool', action: 'create', data: toolData },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data.tool as Tool;
};

export const updateTool = async (id: string, toolData: any): Promise<Tool> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('family-mutate', {
    body: { entity: 'tool', action: 'update', id, data: toolData },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data.tool as Tool;
};

export const deleteTool = async (id: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('family-mutate', {
    body: { entity: 'tool', action: 'delete', id },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

// Upload signing helpers (R2)
type SignUploadResponse = {
  upload_url: string;
  public_url: string;
  headers: Record<string, string>;
  expires_at: string;
  key: string;
};

export const signUpload = async (payload: {
  intent: string; content_type: string;
  task_id?: string; item_id?: string; tool_id?: string; reward_id?: string; event_id?: string; room_id?: string;
  catalog_id?: string;
  /** Standard task template (upload-sign catalog_task_* intents) */
  template_id?: string;
  /** 0-based step index as string (catalog_task_checklist_image) */
  checklist_slot?: string;
}): Promise<SignUploadResponse> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('upload-sign', { body: payload });
  if (error) throw new Error(error.message);
  return data;
};

/**
 * Task header, step and reward pictures (2026-10-13). They used to be kept only in the browser of
 * the device where they were added, so other family devices never saw them. Now they are prepared
 * (HEIC → JPEG, shrunk) and uploaded to the family's folder; the returned URL is what gets saved.
 * For a task, step or reward that doesn't exist yet a temporary id names the folder — the folder
 * only has to be inside the family's own, which the server checks.
 */
export const uploadFamilyPicture = async (
  intent: 'task_header' | 'checklist_image' | 'reward_header',
  file: File | Blob,
  ids: { task_id?: string; item_id?: string; reward_id?: string } = {},
): Promise<string> => {
  // Each step has a time limit, so a hang shows an error naming the step instead of nothing.
  const jpeg = await withTimeout(prepareImageForUpload(file), 45_000, 'prepare');
  const tmp = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now()));
  const signed = await withTimeout(signUpload({
    intent, content_type: 'image/jpeg',
    task_id: intent === 'reward_header' ? undefined : (ids.task_id || tmp()),
    item_id: intent === 'checklist_image' ? (ids.item_id || tmp()) : undefined,
    reward_id: intent === 'reward_header' ? (ids.reward_id || tmp()) : undefined,
  }), 30_000, 'sign');
  await withTimeout(putToSignedUrl(signed.upload_url, signed.headers, jpeg), 90_000, 'upload');
  return signed.public_url;
};

/**
 * Turn a task/step/reward picture a quarter turn clockwise (2026-10-13, task editor): fetch it,
 * rotate it, upload the turned copy and return its URL. `picture` is an uploaded URL or an old
 * device-only image id. The old file is cleaned up when the task is saved without it.
 */
export const rotateFamilyPicture = async (
  intent: 'task_header' | 'checklist_image' | 'reward_header',
  picture: string,
  ids: { task_id?: string; item_id?: string; reward_id?: string } = {},
): Promise<string> => {
  const { rotateImage } = await import('../utils/prepareImage');
  let blob: Blob | undefined;
  if (/^https?:/i.test(picture)) {
    const res = await fetch(picture, { cache: 'no-store' });
    if (!res.ok) throw new Error('Could not load the picture');
    blob = await res.blob();
  } else {
    const { ImageService } = await import('./imageService');
    blob = await ImageService.getImageBlob(picture);
  }
  if (!blob) throw new Error('Could not load the picture');
  return uploadFamilyPicture(intent, await rotateImage(blob, 1), ids);
};

export const signToolImageUpload = async (tool_id: string, content_type: string): Promise<SignUploadResponse> => {
  return signUpload({ intent: 'tool_image', content_type, tool_id });
};

/** Global standard tool catalog (Edge Function; GLOBAL_CATALOG_ADMIN_EMAILS on server). */
export type CatalogToolRow = {
  id: string;
  tool_key: string;
  /** Primary label (typically English) for list views */
  name: string;
  description: string | null;
  name_i18n?: Record<string, string> | null;
  description_i18n?: Record<string, string> | null;
  /** Per-language auto-translation status, e.g. {"da":"needs_review"}. Absent language = not yet translated. */
  i18n_review?: Record<string, string> | null;
  image_url: string | null;
  tool_type: 'tool' | 'supply' | null;
  is_consumable: boolean;
  sort_order: number;
  is_active: boolean;
};

export const catalogToolsList = async (): Promise<CatalogToolRow[]> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-tools', {
    body: { action: 'list' },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return (data as { items?: CatalogToolRow[] })?.items ?? [];
};

export const catalogToolsUpsert = async (payload: Partial<CatalogToolRow> & { tool_key?: string }): Promise<CatalogToolRow> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-tools', {
    body: { action: 'upsert', ...payload },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  const item = (data as { item?: CatalogToolRow })?.item;
  if (!item) throw new Error('Invalid catalog response');
  return item;
};

export const catalogToolsSoftDelete = async (params: { id?: string; tool_key?: string }): Promise<void> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { error } = await getSupabaseForApi().functions.invoke('catalog-tools', {
    body: { action: 'delete', ...params },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
};

/** Auto-translate a standard tool's English name/description into other languages (Claude, server-side). */
export const catalogTranslateTool = async (
  id: string,
  target_langs?: string[],
): Promise<{ item: CatalogToolRow; applied: string[]; failed: string[] }> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-translate', {
    body: { action: 'translate', type: 'tool', id, target_langs },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  const result = data as { item?: CatalogToolRow; applied?: string[]; failed?: string[] };
  if (!result?.item) throw new Error('Invalid translate response');
  return { item: result.item, applied: result.applied ?? [], failed: result.failed ?? [] };
};

/** Master admin: standard task templates (Edge Function catalog-tasks). */
export type CatalogTaskGroupRow = {
  id: string;
  group_key: string | null;
  sort_order: number;
  is_active: boolean;
  created_at?: string;
  task_templates: CatalogTaskTemplateRow[] | null;
};

export type CatalogTaskTemplateRow = {
  id: string;
  group_id: string;
  language_code: string;
  title: string;
  description: string | null;
  room_id: string | null;
  points: number;
  recurrence_days: number | null;
  header_image_url: string | null;
  checklist_items:
    | Array<{ text: string; order_index?: number; image_url?: string | null }>
    | string;
  age_group: AgeGroupCode;
  is_active: boolean;
  tool_keys?: string[];
  suggested_room_original_name?: string | null;
  recreate_on_validation?: boolean;
  days_to_complete?: number | null;
  auto_available?: boolean;
  /** True when auto-translated and not yet reviewed by the owner (English rows are never flagged). */
  needs_review?: boolean;
};

export const catalogTasksList = async (): Promise<CatalogTaskGroupRow[]> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-tasks', {
    body: { action: 'list' },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return (data as { groups?: CatalogTaskGroupRow[] })?.groups ?? [];
};

export const catalogTasksUpsert = async (payload: Record<string, unknown>): Promise<{
  template: CatalogTaskTemplateRow;
  group_id: string;
}> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-tasks', {
    body: { action: 'upsert', ...payload },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  const template = (data as { template?: CatalogTaskTemplateRow })?.template;
  const group_id = (data as { group_id?: string })?.group_id;
  if (!template || !group_id) throw new Error('Invalid catalog-tasks response');
  return { template, group_id };
};

export const catalogTasksSoftDelete = async (params: { group_id?: string; group_key?: string }): Promise<void> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { error } = await getSupabaseForApi().functions.invoke('catalog-tasks', {
    body: { action: 'delete', ...params },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
};

/** Master admin: beta-access invite codes (Edge Function beta-access). */
export type BetaCodeRow = {
  id: string;
  code: string;
  note: string | null;
  max_uses: number;
  uses_count: number;
  is_active: boolean;
  created_at: string;
  beta_code_redemptions: Array<{ email: string; redeemed_at: string }>;
};

export const betaCodesList = async (): Promise<BetaCodeRow[]> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('beta-access', {
    body: { action: 'list' },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return (data as { codes?: BetaCodeRow[] })?.codes ?? [];
};

export const betaCodesCreate = async (params: { code: string; max_uses: number; note?: string }): Promise<BetaCodeRow> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('beta-access', {
    body: { action: 'create', ...params },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  const item = (data as { code?: BetaCodeRow })?.code;
  if (!item) throw new Error('Invalid beta-access response');
  return item;
};

export const betaCodesRevoke = async (id: string): Promise<void> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { error } = await getSupabaseForApi().functions.invoke('beta-access', {
    body: { action: 'revoke', id },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
};

/** Auto-translate a standard task's English title/description/checklist into other languages (Claude, server-side). */
export const catalogTranslateTask = async (
  group_id: string,
  target_langs?: string[],
): Promise<{ templates: Record<string, CatalogTaskTemplateRow>; applied: string[]; failed: string[] }> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-translate', {
    body: { action: 'translate', type: 'task', id: group_id, target_langs },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  const result = data as {
    templates?: Record<string, CatalogTaskTemplateRow>;
    applied?: string[];
    failed?: string[];
  };
  return { templates: result?.templates ?? {}, applied: result?.applied ?? [], failed: result?.failed ?? [] };
};

/** MA-14: mark a translated tool language as reviewed/approved (clears the "needs review" flag). */
export const catalogMarkToolReviewed = async (id: string, lang: string): Promise<CatalogToolRow> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-translate', {
    body: { action: 'mark_reviewed', type: 'tool', id, lang },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  const item = (data as { item?: CatalogToolRow })?.item;
  if (!item) throw new Error('Invalid mark-reviewed response');
  return item;
};

/** MA-14: mark a translated task language as reviewed/approved (clears the "needs review" flag). */
export const catalogMarkTaskReviewed = async (template_id: string): Promise<CatalogTaskTemplateRow> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-translate', {
    body: { action: 'mark_reviewed', type: 'task', template_id },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  const template = (data as { template?: CatalogTaskTemplateRow })?.template;
  if (!template) throw new Error('Invalid mark-reviewed response');
  return template;
};

/**
 * Master admin: cumulative token usage this app's catalog-translate calls have made, with an
 * estimated USD cost (Claude Opus 5 pricing). Anthropic has no "remaining balance" API and Admin
 * API keys require an organization account, so this is a self-tracked estimate covering only
 * this app's translation calls — not true account-wide spend.
 */
export type AnthropicUsageTotals = {
  input_tokens: number;
  output_tokens: number;
  estimated_usd: number;
  updated_at: string | null;
};

export const getAnthropicUsage = async (): Promise<AnthropicUsageTotals> => {
  if (!is_supabase_configured) throw new Error('Global catalog requires Supabase');
  const { data, error } = await getSupabaseForApi().functions.invoke('anthropic-usage', {
    body: { action: 'usage_totals' },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return data as AnthropicUsageTotals;
};

export const putToSignedUrl = async (upload_url: string, headers: Record<string, string>, file: Blob): Promise<void> => {
  const res = await fetch(upload_url, {
    method: 'PUT',
    headers,
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Upload failed: ${res.status} ${text}`);
  }
};

// User methods
export const updateUser = async (
  updates: Partial<User> & { pin?: string; current_pin?: string },
): Promise<User> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('user-profile', {
    body: { action: 'update', ...updates },
  });
  if (error) {
    const { message, code } = await getFunctionsErrorDetails(error);
    throw new ApiError(message, code);
  }
  return data.user;
};

export const uploadAvatarApi = async (file: File): Promise<{ avatar_url: string }> => {
  const jpeg = await prepareImageForUpload(file, 512);
  const signed = await signUpload({ intent: 'avatar', content_type: 'image/jpeg' });
  await putToSignedUrl(signed.upload_url, signed.headers, jpeg);
  // Persist via user-profile Edge Function (service role) so it works in guardian AND
  // PIN-member mode. A direct PostgREST write fails in member mode (rejected JWT) and
  // resolved to no auth user id, which is why uploaded avatars did not persist.
  const { error } = await getSupabaseForApi().functions.invoke('user-profile', {
    body: { action: 'update', avatar_url: signed.public_url },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return { avatar_url: signed.public_url };
};

export const changePassword = async (current_password: string, new_password: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('user-profile', {
    body: { action: 'change-password', current_password, new_password },
  });
  if (error) throw new Error(await getFunctionsErrorMessage(error));
  return;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!authToken;
};

// Get auth token
export const getAuthToken = (): string | null => {
  return authToken;
};

export const resolveAssetUrl = (pathOrUrl?: string | null): string => {
  if (!pathOrUrl) return '';
  // Full URLs (R2, CDN, etc.) are returned as-is
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  // Relative paths like /avatars/* are served from frontend public
  return pathOrUrl;
};

// ── Family stats (docs/design/family-stats.md) ──────────────────────────────────────────────
export const getStatsApi = async (params: {
  period: StatsPeriod; from?: string; to?: string; tz?: string;
}): Promise<StatsResponse> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('stats', {
    body: { action: 'get', ...params },
  });
  if (error) throw await toApiError(error);
  return data as StatsResponse;
};

export const setStatsVisibilityApi = async (visibility: StatsVisibility): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('stats', {
    body: { action: 'set_visibility', visibility },
  });
  if (error) throw await toApiError(error);
};

/** Family setting (admins, 2026-10-19): do grown-ups' tasks need another grown-up's approval? */
export const setAdultsNeedApprovalApi = async (on: boolean): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('family-mutate', {
    body: { entity: 'settings', action: 'update', data: { adults_need_approval: on } },
  });
  if (error) throw await toApiError(error);
};

/** Lemon Squeezy's customer portal for this family's subscription (admins; billing function, 2026-10-13). */
export const getBillingPortalUrl = async (): Promise<string> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('billing', { body: { action: 'portal' } });
  if (error) throw await toApiError(error);
  if (!data?.url) throw new Error('No portal link');
  return data.url as string;
};

// ── Pocket money (2026-10-14; pocket-money function) ─────────────────────────────────────────
export type PocketSettings = {
  enabled: boolean;
  model: 'per_stars' | 'fixed';
  per_ten_amount?: number | null;
  fixed_amount?: number | null;
  threshold_stars?: number | null;
  bonus_amount?: number | null;
  bonus_every?: number | null;
  monthly_cap?: number | null;
  cap_chosen?: boolean;
};
export type PocketChild = {
  member_id: string;
  name: string;
  settings: PocketSettings | null;
  enabled: boolean;
  this_month: { month: string; stars: number; amount: number; capped: boolean; next: { stars: number; adds: number } | null } | null;
  months: Array<{ month: string; stars: number; amount: number; currency: string; paid_at: string | null }>;
};

export const getPocketMoney = async (): Promise<{ currency: string; children: PocketChild[] }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('pocket-money', { body: { action: 'get' } });
  if (error) throw await toApiError(error);
  return data;
};

export const savePocketMoneySettings = async (memberId: string, settings: PocketSettings, currency?: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('pocket-money', { body: { action: 'save', member_id: memberId, settings, currency } });
  if (error) throw await toApiError(error);
};

export const markPocketMoneyPaid = async (memberId: string, month: string, paid = true): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('pocket-money', { body: { action: 'mark_paid', member_id: memberId, month, paid } });
  if (error) throw await toApiError(error);
};

// ── New family setup and standard rewards (2026-10-15) ─────────────────────────────────────────
export type SetupTaskOption = {
  template_id: string; group_id: string; title: string; description: string | null; points: number;
  age_group: string | null; room: string | null; starter: 'per_child' | 'shared'; starter_checked: boolean;
  recreate_on_validation: boolean; recurrence_days: number | null; days_to_complete: number | null; repeat_schedule: any;
};
export type SetupRewardOption = {
  group_id: string; title: string; description: string | null; points_required: number;
  target_type: 'individual' | 'family'; starter_checked: boolean;
};

/** A standard room to pick in the setup: shown as `name`, saved with `english_name` as its original name. */
export type SetupRoomOption = {
  room_key: string; name: string; english_name: string; task_room: string | null; is_child_room: boolean; allow_multiple: boolean;
};

export const getSetupOptions = async (language: string): Promise<{ tasks: SetupTaskOption[]; rewards: SetupRewardOption[]; rooms?: SetupRoomOption[] }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('task-template', { body: { action: 'setup_options', language_code: language } });
  if (error) throw await toApiError(error);
  return data;
};

export const applySetup = async (body: {
  language_code: string;
  tasks: Array<{ template_id: string; assigned_to?: string | null; room_id?: string | null }>;
  rewards: Array<{ group_id: string; target_id?: string | null }>;
}): Promise<{ tasks: number; rewards: number }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('task-template', { body: { action: 'setup', ...body } });
  if (error) throw await toApiError(error);
  return data;
};

export type CatalogRewardGroup = {
  id: string; group_key: string; target_type: 'individual' | 'family'; points_required: number;
  starter_checked: boolean; sort_order: number;
  reward_templates: Array<{ id: string; language_code: string; title: string; description: string | null; needs_review: boolean }>;
};

export const catalogRewardsList = async (): Promise<CatalogRewardGroup[]> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-rewards', { body: { action: 'list' } });
  if (error) throw await toApiError(error);
  return data.groups || [];
};

export const catalogRewardsUpsert = async (payload: Record<string, unknown>): Promise<{ group_id: string }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-rewards', { body: { action: 'upsert', ...payload } });
  if (error) throw await toApiError(error);
  return data;
};

export const catalogRewardsDelete = async (groupId: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('catalog-rewards', { body: { action: 'delete', group_id: groupId } });
  if (error) throw await toApiError(error);
};

export const catalogTranslateReward = async (groupId: string): Promise<{ applied: string[]; failed: string[] }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-translate', { body: { action: 'translate', type: 'reward', id: groupId } });
  if (error) throw await toApiError(error);
  return data;
};

export type CatalogRoomGroup = {
  id: string; room_key: string; task_room: string | null; is_child_room: boolean; allow_multiple: boolean; sort_order: number;
  room_templates: Array<{ id: string; language_code: string; name: string; needs_review: boolean }>;
};

export const catalogRoomsList = async (): Promise<CatalogRoomGroup[]> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-rooms', { body: { action: 'list' } });
  if (error) throw await toApiError(error);
  return data.groups || [];
};

export const catalogRoomsUpsert = async (payload: Record<string, unknown>): Promise<{ group_id: string }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-rooms', { body: { action: 'upsert', ...payload } });
  if (error) throw await toApiError(error);
  return data;
};

export const catalogRoomsDelete = async (groupId: string): Promise<void> => {
  const { error } = await getSupabaseForApi().functions.invoke('catalog-rooms', { body: { action: 'delete', group_id: groupId } });
  if (error) throw await toApiError(error);
};

export const catalogTranslateRoom = async (groupId: string): Promise<{ applied: string[]; failed: string[] }> => {
  const { data, error } = await getSupabaseForApi().functions.invoke('catalog-translate', { body: { action: 'translate', type: 'room', id: groupId } });
  if (error) throw await toApiError(error);
  return data;
};
